<template>
    <div>
        <WdlArchiveFetch
            :is-data-available="!isEmpty(allCompetitors)"
            :is-fetching="!allSeasonsData.isFetchingFinished"
            :place-holder-msg="getStrByLng().MESSAGE_NO_PAST_CHAMPIONS"
        >
            <div class="max-mobile:flex-col max-mobile:flex-nowrap mb-4 flex flex-wrap gap-4">
                <div class="flex flex-col gap-1">
                    <div class="text-sm">{{ getStrByLng().LABEL_SELECT_SEASON }}</div>

                    <WtcMultiSelect
                        v-model="selectedSeasons"
                        class="flex w-72 cursor-pointer items-center border border-black p-2"
                        :empty-message="getStrByLng().MESSAGE_NO_SEASONS"
                        :max-display-options="6"
                        :options="seasonFilterList"
                        :placeholder="getStrByLng().LABEL_ALL_SEASONS"
                        :pt="{
                            overlay: 'bg-white border border-black/50 mt-1 x--theme-scrollbar',
                            dropdown: 'ml-2',
                            root: 'border-content inline-flex items-center justify-between border bg-white px-2 py-1 cursor-pointer select-none',
                            list: 'flex gap-6 text-sm p-3 py-2 pr-5 max-tablet:pr-3',
                            listContainer: 'max-laptop:!max-h-none !max-h-[26rem]',
                            labelContainer: 'overflow-hidden',
                            label: 'overflow-hidden text-ellipsis whitespace-nowrap',
                            optionGroup:
                                'border-l border-black/10 first:border-none pl-4 first:pl-0 py-1 max-tablet:w-full',
                            wtcList: 'max-mobile:grid-flow-row'
                        }"
                    >
                        <template #group-icon="{ allSelected, someSelected }">
                            <img :src="allSelected ? checkActive : someSelected ? checkPartial : checkEmpty" />
                        </template>
                        <template #item-icon="{ isSelected }">
                            <img :src="isSelected ? checkActive : checkEmpty" />
                        </template>
                    </WtcMultiSelect>
                </div>

                <div class="flex flex-col gap-1">
                    <div class="text-sm">{{ getStrByLng().LABEL_SELECT_DISCIPLINE }}</div>
                    <WtcMultiSelect
                        v-model="selectedDisciplines"
                        class="flex w-72 cursor-pointer items-center border border-black p-2"
                        :empty-message="getStrByLng().MESSAGE_NO_DISCIPLINES"
                        expandable-options
                        :max-display-options="3"
                        :options="groupedDisciplinesList"
                        :placeholder="getStrByLng().LABEL_ALL_DISCIPLINES"
                        :pt="{
                            overlay: 'bg-white border border-black/50 mt-1 max-mobile:w-full x--theme-scrollbar',
                            dropdown: 'ml-2',
                            root: 'border-content inline-flex items-center justify-between border bg-white px-2 py-1 cursor-pointer select-none',
                            list: 'flex gap-6 text-sm p-3 py-2 pr-5 max-tablet:flex-col max-tablet:gap-2 max-tablet:px-2',
                            labelContainer: 'overflow-hidden',
                            label: 'overflow-hidden text-ellipsis whitespace-nowrap',
                            listContainer: 'max-laptop:!max-h-none !max-h-[26rem]',
                            optionGroup:
                                'border-l border-black/10 first:border-none pl-4 first:pl-0 py-1 max-laptop:pl-0 max-laptop:border-l-0 max-tablet:border-t max-tablet:pt-3 max-laptop:first:pt-1',
                            wtcList: '!grid-rows-10 max-laptop:grid-flow-row'
                        }"
                    >
                        <template #group-icon="{ allSelected, someSelected }">
                            <img :src="allSelected ? checkActive : someSelected ? checkPartial : checkEmpty" />
                        </template>
                        <template #item-icon="{ isSelected }">
                            <img :src="isSelected ? checkActive : checkEmpty" />
                        </template>
                    </WtcMultiSelect>
                </div>
            </div>

            <ul class="max-tablet:grid-cols-1 grid grid-cols-2 gap-3">
                <li v-for="(competitors, key) in athletesGroupedByDiscipline" :key="key">
                    <div class="font-normal_medium mb-1">{{ competitors[0]?.DisciplineName }}</div>
                    <table class="w-full">
                        <thead>
                            <tr>
                                <th class="w-16 min-w-16">Year</th>
                                <th class="text-left">Athlete</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="competitor in competitors" :key="competitor.Id">
                                <td>{{ competitor.SeasonCode }}</td>
                                <td class="text-left">
                                    <div class="flex gap-1">
                                        <NocFlag :nat-code="competitor.Nat" />
                                        <WdlArchiveName :competitor></WdlArchiveName>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </li>
            </ul>
        </WdlArchiveFetch>
    </div>
</template>

<script setup lang="ts">
import { getStrByLng, IWtcMultiSelectItem, IWtcMultiSelectOptions, WtcMultiSelect } from "@swisstiming/webtec-kit";
import { useFetch } from "@vueuse/core";
import { groupBy, isEmpty, isNil, orderBy, uniqBy } from "lodash-es";
import { computed, reactive, ref, watch } from "vue";

import checkActive from "../assets/images/check-active.svg";
import checkEmpty from "../assets/images/check-empty.svg";
import checkPartial from "../assets/images/check-partial.svg";
import NocFlag from "../components/elements/NocFlag.vue";
import WdlArchiveName from "../components/elements/WdlArchiveName.vue";
import WdlArchiveFetch from "../components/WdlArchiveFetch.vue";
import { IDiamondRaceJson } from "../interfaces/wdl-archive.interfaces";
import { useArchiveStore } from "../stores/wdl-archive.store";

const selectedDisciplines = ref<IWtcMultiSelectItem[]>([]);
const selectedSeasons = ref<IWtcMultiSelectItem[]>([]);

const selectedDisciplineValues = computed(() => selectedDisciplines.value.map((disciplineGrp) => disciplineGrp.value));
const selectedSeasonsValues = computed(() => selectedSeasons.value.map((seasonGrp) => seasonGrp.value));

const archiveStore = useArchiveStore();

const { seasonsData, isFinished: seasonDataRequestFinished } = archiveStore.useSeasons();

const allSeasonsData = reactive<{ isFetchingFinished: boolean; data: { [p: string]: IDiamondRaceJson } }>({
    isFetchingFinished: false,
    data: {}
});

watch(seasonDataRequestFinished, (isSeasonDataAvailable) => {
    if (isSeasonDataAvailable) {
        const fetches = seasonsData.value.map((season) => {
            const { data, isFinished } = useFetch(archiveStore.getDiamondRaceJsonUrl(season), {
                refetch: true,
                updateDataOnError: true,
                onFetchError(ctx) {
                    ctx.data = undefined;
                    return ctx;
                }
            }).json<IDiamondRaceJson>();
            // @ts-ignore
            allSeasonsData.data[season] = data;
            return { isFinished };
        });
        // @ts-ignore
        allSeasonsData.isFetchingFinished = computed(() => fetches.every(({ isFinished }) => isFinished.value));
    }
});

// WORKAROUND because of inconsistent discipline-codes!
// The discipline-codes provided via the 'Code'-property in the DiamondRaceJson are not consistent.
// This function unifies the discipline-codes to a consistent format.
// This might need adjustments if future inconsistencies are discovered.
const unifyDisciplineCodes = (code: string) => {
    const nomalizedCode = code.replaceAll(" ", "").toLowerCase();
    switch (nomalizedCode) {
        // men
        case "3000msteeplechasemen":
        case "3000mscmen":
            return "3000mscmen";
        case "110mhurdlesmen":
        case "110mhmen":
            return "110mhmen";
        case "400mhurdlesmen":
        case "400mhmen":
            return "400mhmen";
        case "highjumpmen":
            return "highjumpmen";
        case "polevaultmen":
            return "polevaultmen";
        case "longjumpmen":
            return "longjumpmen";
        case "triplejumpmen":
            return "triplejumpmen";
        case "shotputmen":
            return "shotputmen";
        case "discusthrowmen":
            return "discusthrowmen";
        case "javelinthrowmen":
            return "javelinthrowmen";
        // women
        case "3000msteeplechasewomen":
        case "3000mscwomen":
            return "3000mscwomen";
        case "100mhurdleswomen":
        case "100mhwomen":
            return "100mhwomen";
        case "400mhurdleswomen":
        case "400mhwomen":
            return "400mhwomen";
        case "highjumpwomen":
            return "highjumpwomen";
        case "polevaultwomen":
            return "polevaultwomen";
        case "longjumpwomen":
            return "longjumpwomen";
        case "triplejumpwomen":
            return "triplejumpwomen";
        case "shotputwomen":
            return "shotputwomen";
        case "discusthrowwomen":
            return "discusthrowwomen";
        case "javelinthrowwomen":
            return "javelinthrowwomen";
        default:
            return code;
    }
};

const allCompetitors = computed(() => {
    if (allSeasonsData.isFetchingFinished) {
        return Object.entries(allSeasonsData.data ?? {})
            .filter(([_, diamondData]) => diamondData?.Statistics?.DiamondRaceFinished)
            .flatMap(([season, diamondData]) =>
                [
                    ...Object.values(diamondData?.Data?.Men ?? {}).map((menDiscipline) => {
                        return { ...menDiscipline, Gender: "M" };
                    }),
                    ...Object.values(diamondData?.Data?.Women ?? {}).map((womenDiscipline) => {
                        return { ...womenDiscipline, Gender: "W" };
                    })
                ].flatMap(({ Athletes, Code, Name, Gender, Winners }) =>
                    Athletes?.filter((athlete) => {
                        // WORKAROUND because of inconsistent winner declaration
                        // The winner declaration is not consistent in the DiamondRaceJson.
                        // The winner should be the athlete with the 'Rank' == 1, which is not the case for 2023 and 2024.
                        // For seasons, especially prior to 2017, the 'Winners'-property might not exist.
                        // For 2023 and 2024 the winner is provided in the 'Winners'-property.
                        // 'FirstName'- and 'LastName'-property are used to reference to the athlete in the 'Athletes'-list
                        // because we need the 'AthleteId' to link to an external athlete detail page.

                        if (Winners?.length > 0) {
                            return Winners.some(
                                (winner) =>
                                    // Need to lowercase because names in 'Winners'-list can differ in case from 'Athletes'-list
                                    (winner.FirstName?.toLowerCase() === athlete.FirstName?.toLowerCase() &&
                                        winner.LastName?.toLowerCase() === athlete.LastName?.toLowerCase()) ||
                                    // This need to be checked because for certain athletes, i.e. koreans, the first and last name are swapped.
                                    (winner.LastName?.toLowerCase() === athlete.FirstName?.toLowerCase() &&
                                        winner.FirstName?.toLowerCase() === athlete.LastName?.toLowerCase())
                            );
                        } else {
                            return athlete.Rank === 1;
                        }
                    }).map((athlete) => {
                        return {
                            ...athlete,
                            SeasonCode: season,
                            DisciplineCode: unifyDisciplineCodes(Code),
                            DisciplineName: Name,
                            DisciplineGender: Gender
                        };
                    })
                )
            );
    }

    return undefined;
});

const allCompetitorsFiltered = computed(() => {
    if (selectedSeasons.value.length > 0 || selectedDisciplines.value.length > 0) {
        return allCompetitors.value.filter((athlete) => {
            const isFilteredBySeason =
                selectedSeasonsValues.value.length > 0
                    ? selectedSeasonsValues.value.includes(athlete.SeasonCode)
                    : true;
            const isFilteredByDiscipline =
                selectedDisciplineValues.value.length > 0
                    ? selectedDisciplineValues.value.includes(athlete.DisciplineCode)
                    : true;

            return isFilteredBySeason && isFilteredByDiscipline;
        });
    }
    return allCompetitors.value;
});

const allCompetitorsSorted = computed(() => orderBy(allCompetitorsFiltered.value, (item) => item.SeasonCode, "desc"));
const athletesGroupedByDiscipline = computed(() =>
    groupBy(allCompetitorsSorted.value, (athlete) => athlete.DisciplineCode)
);

const allUniqueWomenDisciplines = computed(() =>
    uniqBy(
        allCompetitors.value.filter((athlete) => athlete.DisciplineGender === "W"),
        (athlete) => athlete.DisciplineCode
    )
);

const allUniqueMenDisciplines = computed(() =>
    uniqBy(
        allCompetitors.value.filter((athlete) => athlete.DisciplineGender === "M"),
        (athlete) => athlete.DisciplineCode
    )
);

const groupedDisciplinesList = computed<IWtcMultiSelectOptions[]>(() => [
    {
        label: getStrByLng().LABEL_ALL_MEN_DISCIPLINES,
        items: allUniqueMenDisciplines.value?.map((discipline) => {
            return {
                label: discipline.DisciplineName,
                value: discipline.DisciplineCode
            };
        })
    },
    {
        label: getStrByLng().LABEL_ALL_WOMEN_DISCIPLINES,
        items: allUniqueWomenDisciplines.value?.map((discipline) => {
            return {
                label: discipline.DisciplineName,
                value: discipline.DisciplineCode
            };
        })
    }
]);

const availableSeasons = computed(() =>
    Object.entries(allSeasonsData.data ?? {})
        .filter(([key, value]) => !isNil(value))
        .map(([key]) => key)
);

const seasonFilterList = computed<IWtcMultiSelectOptions[]>(() => [
    {
        label: getStrByLng().LABEL_ALL_SEASONS,
        items: orderBy(
            availableSeasons.value?.map((season) => {
                return {
                    label: season,
                    value: season
                };
            }),
            (item) => item.value,
            "desc"
        )
    }
]);
</script>

<style lang="scss"></style>
